/**
 * Редактор Раскладки — то, что происходит между рукой Администратора и сервером
 * (спецификация § 4, тикет T18).
 *
 * Сетка (`grid.ts`) знает, куда встанут прямоугольники. Редактор знает остальное: человек
 * видит движение сразу, не дожидаясь сервера; при отказе Раскладка возвращается ровно
 * такой, какой была; на сервер уходят только те Блоки, которые действительно сдвинулись.
 *
 * Ни браузера, ни React здесь нет (ADR-0002): сервер спрятан за портом, показ — за
 * подпиской. Ту же самую работу выполнит и настольное приложение, и телефон.
 */
import { addBlock as placeBlock, moveBlock, movedBlocks, removeBlock, resizeBlock } from './grid.js';
import type { GridChange, PlacedBlock, Position } from './grid.js';

/** Тип Блока из каталога — ровно то, что нужно списку «добавить Блок». */
export interface BlockKind {
  readonly id: string;
  /** Название уже на языке смотрящего: переводит его сервер (§ 11). */
  readonly title: string;
  /**
   * Одна строка о том, что Блок делает, — на языке смотрящего. Пустая, если сервер
   * описания не прислал: список «добавить Блок» тогда покажет одно название, как раньше.
   */
  readonly about: string;
  readonly defaultSize: { readonly width: number; readonly height: number };
  /** Меньше этого Блок не делается: у Переписки и у Котировок пределы разные. */
  readonly minimumSize?: { readonly width: number; readonly height: number };
}

/** Насколько крупные буквы в Блоке. Пусто — как везде на этом экране. */
export const BLOCK_TEXT_SCALES = ['small', 'normal', 'large', 'huge'] as const;
export type BlockTextScale = (typeof BLOCK_TEXT_SCALES)[number];

/**
 * Как Блок выглядит у одного смотрящего. Это не свойство Блока, а свойство экрана: сосед
 * по Каналу видит тот же Блок развёрнутым и в общих красках.
 *
 * Скрытый Блок из списка не выбрасывается — иначе его нельзя было бы вернуть: показывать
 * или нет, решает оболочка, а ядро обязано помнить, что он вообще есть.
 */
export interface BlockView {
  /** Свёрнут: видна одна шапка. */
  readonly collapsed: boolean;
  /** Скрыт с экрана. Возвращается из списка скрытых. */
  readonly hidden: boolean;
  /** Закреплён: не берётся ни мышью, ни клавиатурой. */
  readonly pinned: boolean;
  /** Цвет фона, `#rrggbb`. Пусто — общий. */
  readonly background: string | null;
  /** Цвет букв, `#rrggbb`. Пусто — общий. */
  readonly foreground: string | null;
  readonly textScale: BlockTextScale | null;
}

/** Правка вида: только названные поля, остальное остаётся как было. */
export interface BlockViewPatch {
  readonly collapsed?: boolean;
  readonly hidden?: boolean;
  readonly pinned?: boolean;
  readonly background?: string | null;
  readonly foreground?: string | null;
  readonly textScale?: BlockTextScale | null;
}

/** Обычный вид: ничего не свёрнуто, не скрыто, не закреплено, краски общие. */
export const DEFAULT_BLOCK_VIEW: BlockView = {
  collapsed: false,
  hidden: false,
  pinned: false,
  background: null,
  foreground: null,
  textScale: null,
};

/** Блок на Раскладке так, как его отдаёт сервер, вместе с личным видом смотрящего. */
export interface Block extends PlacedBlock {
  readonly kind: string;
  readonly visibleToGuest: boolean;
  readonly view: BlockView;
}

/** Раскладка, как её отдал сервер: Блоки и настроена ли она под смотрящего. */
export interface LayoutSnapshot {
  readonly blocks: Block[];
  /** Правда — человек двигал Блоки под себя и видит не то же, что соседи по Каналу. */
  readonly personal: boolean;
}

/**
 * Личная Раскладка: свой порядок Блоков поверх общего. Порт необязательный — у заготовок
 * Пространства и Отдела личных мест не бывает, там просто некому смотреть.
 */
export interface PersonalGateway {
  /** Запомнить моё место Блока. Общая Раскладка не меняется. */
  setPlacement(input: { blockId: string; position: Position }): Promise<void>;
  /** Запомнить, как Блок выглядит у меня. Возвращает вид целиком, как его видит сервер. */
  setView(input: { blockId: string; view: BlockViewPatch }): Promise<BlockView>;
  /** Стереть все мои места и виды — вернуться к общему виду. */
  clear(): Promise<void>;
}

/**
 * Дверь к серверу. Редактор ходит только сюда: подменив порт, ту же работу можно
 * проверить без сети, а оболочку — заменить, не трогая ядро.
 */
export interface LayoutGateway {
  /** Раскладка целиком. */
  readLayout(): Promise<LayoutSnapshot>;
  /** Каталог Типов на языке смотрящего. */
  readKinds(): Promise<BlockKind[]>;
  addBlock(input: { kind: string; position: Position }): Promise<Block>;
  updateBlock(input: { blockId: string; position: Position }): Promise<void>;
  removeBlock(blockId: string): Promise<void>;
  /** Личные места. Нет порта — личный режим редактору недоступен. */
  readonly personal?: PersonalGateway;
}

/** Что видно на экране прямо сейчас. */
export interface EditorState {
  readonly blocks: readonly Block[];
  readonly kinds: readonly BlockKind[];
  /** Раскладка ещё читается с сервера — показывать нечего. */
  readonly loading: boolean;
  /** Идёт разговор с сервером: пока идёт, вторую правку не начинают. */
  readonly saving: boolean;
  /** Объяснение словами, если последняя правка не прошла. Иначе пусто. */
  readonly refusal: string | null;
  /** Настроена ли Раскладка под смотрящего: есть ли, что возвращать «как у всех». */
  readonly personal: boolean;
  /**
   * Куда уходят правки места: в общую Раскладку или только себе. Личный режим доступен
   * всем, кому виден Канал, общий — одному Администратору.
   */
  readonly mode: 'shared' | 'personal';
}

export interface LayoutEditor {
  state(): EditorState;
  /** Узнать об изменениях. Возвращает способ отписаться. */
  subscribe(listener: (state: EditorState) => void): () => void;
  load(): Promise<void>;
  move(id: string, to: { col: number; row: number }): Promise<void>;
  resize(id: string, size: { width: number; height: number }): Promise<void>;
  add(kind: string, at?: { col: number; row: number }): Promise<void>;
  remove(id: string): Promise<void>;
  /**
   * Настроить, как Блок выглядит у меня: свернуть, скрыть, закрепить, покрасить. Правка
   * всегда личная, в каком бы режиме ни стоял редактор: краски и свёрнутость — свойство
   * одного экрана, и уводить их в общую Раскладку было бы уже другим действием.
   */
  setView(id: string, patch: BlockViewPatch): Promise<void>;
  /**
   * Переключить, куда идут правки места: «под себя» или в общую Раскладку. Порта личных
   * мест нет — переключение молча не проходит, состояние останется общим.
   */
  setMode(mode: 'shared' | 'personal'): void;
  /** Вернуть как у всех: стереть свои места и перечитать Раскладку с сервера. */
  resetPersonal(): Promise<void>;
  /** Убрать объяснение с глаз, когда человек его прочитал. */
  dismissRefusal(): void;
}

/** Сорвалось на полпути: сколько правок сервер успел принять до беды. */
interface SaveFailure {
  readonly error: unknown;
  readonly saved: number;
}

/**
 * Беда, о которой не сказали словами, — это пустой экран без объяснения. Сюда попадает
 * всё, чего мы не ждали: оборванная сеть, чужой ответ, молчание сервера.
 */
const explain = (error: unknown): string => {
  const said = error instanceof Error ? error.message.trim() : '';
  return said || 'Не получилось сохранить — проверьте связь и попробуйте ещё раз';
};

/** Наименьший размер Типа; не задан — хотя бы одна клетка. */
const minimumOf = (
  kinds: readonly BlockKind[],
  kindId: string,
): { width: number; height: number } => {
  const kind = kinds.find((one) => one.id === kindId);
  return kind?.minimumSize ? { ...kind.minimumSize } : { width: 1, height: 1 };
};

const BUSY = 'Предыдущая правка ещё сохраняется — подождите мгновение';

/**
 * Состав Блоков в Канале общий: что там стоит, решает Администратор. Под себя двигают
 * места, а не набор — иначе «посмотри во втором Блоке» перестало бы значить одно и то же
 * для двух человек.
 */
const ONLY_PLACES = 'Под себя настраивается только расположение Блоков, а не их набор';

/**
 * Закреплённый Блок не берётся руками. Отказ объясняется словами и называет выход: молча
 * «не двигается» человек принял бы за поломку, а не за своё же решение.
 */
const PINNED = 'Блок закреплён — снимите закрепление в его меню, чтобы двигать';

/** Личный вид настраивается там, где есть кому смотреть, — то есть в Канале. */
const NO_PERSONAL = 'Здесь вид Блока под себя не настраивается';

export function createLayoutEditor(gateway: LayoutGateway): LayoutEditor {
  let blocks: readonly Block[] = [];
  let kinds: readonly BlockKind[] = [];
  let loading = false;
  let saving = false;
  let refusal: string | null = null;
  let personal = false;
  let mode: 'shared' | 'personal' = 'shared';
  const listeners = new Set<(state: EditorState) => void>();

  const state = (): EditorState => ({ blocks, kinds, loading, saving, refusal, personal, mode });
  const tell = (): void => {
    const now = state();
    for (const listener of listeners) listener(now);
  };

  /** Закреплён ли Блок. Неизвестный Блок закреплённым не считается — о нём скажет сетка. */
  const isPinned = (id: string): boolean =>
    blocks.find((block) => block.id === id)?.view.pinned === true;

  /** Расстановка из сетки обратно в Блоки: Тип и отметка «видно Гостю» не теряются. */
  const merge = (placed: readonly PlacedBlock[]): Block[] =>
    placed.map((one) => {
      const known = blocks.find((block) => block.id === one.id);
      // Неизвестный Блок бывает только у добавления, а там свой путь — сюда не попадает.
      if (!known) throw new Error(`Блок «${one.id}» потерялся при перестановке`);
      return { ...known, position: one.position };
    });

  /**
   * Сохранить сдвинувшихся — по одному, по порядку. Беду не бросаем наружу, а возвращаем
   * вместе с числом принятых правок: чинить экран после срыва на первом Блоке и после
   * срыва на третьем нужно по-разному.
   */
  const saveMoved = async (moved: readonly PlacedBlock[]): Promise<SaveFailure | null> => {
    let saved = 0;
    for (const block of moved) {
      try {
        // В личном режиме место уходит только мне: общая Раскладка Канала не шелохнётся,
        // и соседи по Каналу ничего не заметят. Расталкивание соседей при этом тоже
        // остаётся личным — они двигаются на моём экране и сохраняются мне же.
        if (mode === 'personal' && gateway.personal) {
          await gateway.personal.setPlacement({ blockId: block.id, position: block.position });
        } else {
          await gateway.updateBlock({ blockId: block.id, position: block.position });
        }
      } catch (error) {
        return { error, saved };
      }
      saved += 1;
      if (mode === 'personal') personal = true;
    }
    return null;
  };

  /** Перечитать Раскладку молча. Не вышло и это — пусть решает тот, кто позвал. */
  const reread = async (): Promise<boolean> => {
    try {
      const snapshot = await gateway.readLayout();
      blocks = snapshot.blocks;
      personal = snapshot.personal;
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Починить экран после сорвавшегося сохранения.
   *
   * Сервер принял часть правок — местный откат соврал бы: человек увидел бы прежнюю
   * Раскладку, а на другом устройстве она уже другая. Поэтому при частичном успехе
   * спрашиваем сервер заново, и только если он молчит — возвращаем то, что было.
   */
  const recover = async (before: readonly Block[], failure: SaveFailure): Promise<void> => {
    if (failure.saved > 0 && (await reread())) {
      refusal = `${explain(failure.error)}. Часть Блоков уже сохранилась — Раскладка показана заново`;
      return;
    }
    blocks = before;
    refusal = explain(failure.error);
  };

  /**
   * Общий ход перетаскивания и изменения размера: показать движение сразу, отправить
   * сдвинувшихся, а при беде вернуть Раскладку к правде.
   */
  const apply = async (change: GridChange): Promise<void> => {
    if (change.refusal) {
      refusal = change.refusal;
      tell();
      return;
    }
    if (saving || loading) {
      refusal = BUSY;
      tell();
      return;
    }

    const before = blocks;
    const after = merge(change.blocks);
    const moved = movedBlocks(before, after);

    blocks = after;
    saving = true;
    refusal = null;
    tell();

    const failure = await saveMoved(moved);
    if (failure) await recover(before, failure);
    saving = false;
    tell();
  };

  return {
    state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async load() {
      loading = true;
      tell();
      try {
        const [snapshot, loadedKinds] = await Promise.all([
          gateway.readLayout(),
          gateway.readKinds(),
        ]);
        blocks = snapshot.blocks;
        personal = snapshot.personal;
        kinds = loadedKinds;
        refusal = null;
      } catch (error) {
        refusal = explain(error);
      } finally {
        loading = false;
        tell();
      }
    },

    async move(id, to) {
      if (isPinned(id)) {
        refusal = PINNED;
        tell();
        return;
      }
      await apply(moveBlock(blocks, id, to));
    },

    async resize(id, size) {
      if (isPinned(id)) {
        refusal = PINNED;
        tell();
        return;
      }
      const kind = blocks.find((block) => block.id === id)?.kind;
      const minimum = kind ? minimumOf(kinds, kind) : { width: 1, height: 1 };
      await apply(resizeBlock(blocks, id, size, minimum));
    },

    /**
     * Добавление идёт иначе: у нового Блока ещё нет номера, его даёт сервер. Сначала
     * спрашиваем сервер, и только потом Блок появляется на экране — выдуманный номер
     * разошёлся бы с настоящим на первой же следующей правке.
     */
    async add(kind, at) {
      if (saving || loading) {
        refusal = BUSY;
        tell();
        return;
      }
      if (mode === 'personal') {
        refusal = ONLY_PLACES;
        tell();
        return;
      }
      const known = kinds.find((one) => one.id === kind);
      if (!known) {
        refusal = 'Такого Блока нет в списке';
        tell();
        return;
      }

      // Место считаем на временном имени — чтобы упереться в предел Блоков и в край
      // сетки до похода на сервер, а не после.
      const DRAFT = 'черновик';
      const draft = placeBlock(blocks, {
        id: DRAFT,
        size: { ...known.defaultSize },
        ...(at ? { at } : {}),
      });
      if (draft.refusal) {
        refusal = draft.refusal;
        tell();
        return;
      }
      const spot = draft.blocks.find((one) => one.id === DRAFT)?.position;
      if (!spot) throw new Error('Место для нового Блока не нашлось');

      const before = blocks;
      saving = true;
      refusal = null;
      tell();

      let added: Block;
      try {
        added = await gateway.addBlock({ kind, position: spot });
      } catch (error) {
        refusal = explain(error);
        saving = false;
        tell();
        return;
      }

      // Новый Блок встал — теперь досаживаем соседей, которых он потеснил. Всё, что
      // ниже, идёт под присмотром: беда здесь оставила бы экран навсегда в «сохраняется»,
      // а Блок на сервере — без соседей на новых местах.
      try {
        blocks = draft.blocks.map((one) => {
          if (one.id === DRAFT) return { ...added, position: one.position };
          const known = before.find((block) => block.id === one.id);
          if (!known) throw new Error(`Блок «${one.id}» потерялся при перестановке`);
          return { ...known, position: one.position };
        });
        const neighbours = movedBlocks(before, blocks).filter((one) => one.id !== added.id);
        const failure = await saveMoved(neighbours);
        // Блок на сервере уже есть — откатывать экран нельзя, спрашиваем правду.
        if (failure) await recover(before, { error: failure.error, saved: failure.saved + 1 });
      } catch (error) {
        await recover(before, { error, saved: 1 });
      } finally {
        saving = false;
        tell();
      }
    },

    async remove(id) {
      // Занятость проверяется первой — тем же порядком, что и в `apply`: разойдись он,
      // однажды правка началась бы поверх недосохранённой.
      if (saving || loading) {
        refusal = BUSY;
        tell();
        return;
      }
      if (mode === 'personal') {
        refusal = ONLY_PLACES;
        tell();
        return;
      }
      const change = removeBlock(blocks, id);
      if (change.refusal) {
        refusal = change.refusal;
        tell();
        return;
      }

      const before = blocks;
      const after = merge(change.blocks);
      blocks = after;
      saving = true;
      refusal = null;
      tell();

      try {
        await gateway.removeBlock(id);
      } catch (error) {
        blocks = before;
        refusal = explain(error);
        saving = false;
        tell();
        return;
      }

      // Поднявшихся соседей сохраняем следом: иначе на другом устройстве они остались бы
      // висеть вокруг дыры от убранного Блока.
      const failure = await saveMoved(movedBlocks(before, after));
      if (failure) await recover(before, { error: failure.error, saved: failure.saved + 1 });
      saving = false;
      tell();
    },

    /**
     * Свернуть, скрыть, закрепить, покрасить — всё это правки одного личного вида и идут
     * одним путём. Показываем сразу, а при отказе возвращаем прежний вид: свёрнутый Блок,
     * который «свернулся» на экране и остался развёрнутым на сервере, обманул бы человека
     * до первой перезагрузки.
     */
    async setView(id, patch) {
      if (!gateway.personal) {
        refusal = NO_PERSONAL;
        tell();
        return;
      }
      if (saving || loading) {
        refusal = BUSY;
        tell();
        return;
      }
      const target = blocks.find((block) => block.id === id);
      if (!target) {
        refusal = 'Такого Блока на Раскладке нет';
        tell();
        return;
      }

      const before = blocks;
      blocks = blocks.map((block) =>
        block.id === id ? { ...block, view: { ...block.view, ...patch } } : block,
      );
      saving = true;
      refusal = null;
      tell();

      try {
        // Сервер отвечает видом целиком — берём его, а не свою догадку: он один знает,
        // что получилось после проверки цвета и слияния с прежними настройками.
        const saved = await gateway.personal.setView({ blockId: id, view: patch });
        blocks = blocks.map((block) => (block.id === id ? { ...block, view: saved } : block));
        personal = true;
      } catch (error) {
        blocks = before;
        refusal = explain(error);
      } finally {
        saving = false;
        tell();
      }
    },

    setMode(next) {
      // Порта личных мест нет — молча остаёмся в общем режиме: у заготовки Пространства
      // личной расстановки не бывает, и предлагать её там нечего.
      if (next === 'personal' && !gateway.personal) return;
      if (mode === next) return;
      mode = next;
      refusal = null;
      tell();
    },

    /**
     * Вернуть как у всех. Перечитываем Раскладку с сервера, а не откатываем по памяти:
     * общая расстановка могла за это время смениться, и «как у всех» должно означать
     * сегодняшнее общее, а не то, каким оно было при первой личной правке.
     */
    async resetPersonal() {
      if (!gateway.personal) return;
      if (saving || loading) {
        refusal = BUSY;
        tell();
        return;
      }
      saving = true;
      refusal = null;
      tell();
      try {
        await gateway.personal.clear();
        personal = false;
        if (!(await reread())) {
          refusal = 'Свои места стёрлись, но Раскладку перечитать не вышло — обновите страницу';
        }
      } catch (error) {
        refusal = explain(error);
      } finally {
        saving = false;
        tell();
      }
    },

    dismissRefusal() {
      if (refusal === null) return;
      refusal = null;
      tell();
    },
  };
}
